import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "../../contexts/ToastContext";
import { billingService } from "../../services/billing";
import { projectsService } from "../../services/projects";
import { getApiErrorMessage } from "../../utils/axios";
import { sitePath } from "../../utils/urls";

const TransferSite = () => {
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const [projects, setProjects] = useState([]);
  const [creditId, setCreditId] = useState(null);
  const [projectHash, setProjectHash] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let done = 0;
    const finish = () => {
      done += 1;
      if (done >= 2) setLoading(false);
    };

    billingService.listPurchasedSites(
      (res) => {
        const unlinked = (res.data || []).find((row) => !row.linked);
        setCreditId(unlinked?.id || null);
        finish();
      },
      () => finish(),
    );
    projectsService.listProjects({ archived: false, page_size: 500 }, (res) => {
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setProjects(list.filter((p) => !p.purchased_site));
      finish();
    }, () => finish());
  }, []);

  const close = () => navigate("/account/billing/");

  const goToLaunch = () => {
    navigate("/account/billing/#plans");
  };

  const submitTransfer = (event) => {
    event.preventDefault();
    if (!creditId || !projectHash || !confirmed) return;

    const project = projects.find((p) => p.hash === projectHash);
    if (!project) return;

    const ok = window.confirm(
      `Transfer "${project.name}" permanently? This cannot be reversed. Export downloads will unlock in site settings, but Weave hosting features stay locked unless you subscribe.`,
    );
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    billingService.linkPurchasedSite(
      creditId,
      projectHash,
      () => {
        // Full load so WorkspaceBootstrap picks up purchased_site for export buttons.
        window.location.assign(sitePath(project, "settings/management/"));
      },
      (requestError) => {
        setSubmitting(false);
        setError(getApiErrorMessage(requestError, "Could not transfer this site."));
        toastError(getApiErrorMessage(requestError, "Could not transfer this site."));
      },
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-theme-secondary px-6 py-4">
        <div>
          <h2 className="text-xl font-bold text-theme-text">Transfer a site</h2>
          <p className="mt-0.5 text-sm text-theme-text opacity-70">
            Use a handoff credit to take a site with you.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded-lg p-2 text-theme-text opacity-70 transition-colors hover:bg-theme-secondary hover:opacity-100"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <p className="text-sm opacity-60">Loading…</p>
        ) : !creditId ? (
          <div className="space-y-4">
            <p className="text-sm opacity-70">You do not have an unused handoff credit.</p>
            <button
              type="button"
              onClick={close}
              className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold"
            >
              Back to billing
            </button>
          </div>
        ) : (
          <form onSubmit={submitTransfer} className="space-y-5">
            {error && (
              <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-40 px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2 text-sm text-theme-text opacity-80">
              <p>
                Transfer links your handoff credit to one site. Afterward you can download
                your data and codebase from site settings and host the site yourself.
              </p>
              <p className="font-medium text-theme-text opacity-100">What you get</p>
              <ul className="list-disc space-y-1 pl-5 opacity-80">
                <li>Data export (CSV / JSON)</li>
                <li>Codebase download when available</li>
                <li>Freedom to host and develop outside Weave</li>
              </ul>
              <p className="font-medium text-theme-text opacity-100">What you give up on Weave</p>
              <ul className="list-disc space-y-1 pl-5 opacity-80">
                <li>Ongoing coding and data agent changes</li>
                <li>Site audits, analytics, and forms</li>
                <li>Custom domain and Weave-hosted deployment</li>
              </ul>
            </div>

            <div className="rounded-lg border border-theme-secondary p-4">
              <p className="text-sm font-medium">Want to keep building on Weave?</p>
              <p className="mt-1 text-xs opacity-70">
                Launch includes future coding and data changes, site audits, analytics, and
                hosting for just $15/mo.
              </p>
              <button
                type="button"
                onClick={goToLaunch}
                className="mt-3 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button"
              >
                Get Launch Plan Instead
              </button>
            </div>

            <label className="block">
              <span className="text-sm font-medium">Site to transfer</span>
              <select
                value={projectHash}
                onChange={(e) => setProjectHash(e.target.value)}
                required
                className="mt-2 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
              >
                <option value="">Select a site…</option>
                {projects.map((p) => (
                  <option key={p.hash} value={p.hash}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1"
                required
              />
              <span className="opacity-80">
                I understand this transfer cannot be reversed. This credit will be permanently
                linked to the selected site.
              </span>
            </label>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={submitting || !projectHash || !confirmed}
                className={`rounded-lg px-5 py-2.5 font-medium disabled:opacity-50 ${
                  projectHash && confirmed
                    ? "bg-theme-primary text-theme-button"
                    : "border border-theme-secondary"
                }`}
              >
                {submitting ? "Transferring…" : "Confirm transfer"}
              </button>
              <button
                type="button"
                onClick={close}
                className="text-sm opacity-70 hover:opacity-100"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TransferSite;
